import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Mail, Instagram, MessageCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSetting } from "@/context/CmsContext";
import { useLocale } from "@/context/LocaleContext";
import Logo from "./Logo";
import LocaleSwitch from "./LocaleSwitch";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const { tr, en } = useLocale();
    const contactEmail = useSetting("contact_email", "ola@novifood.pt");
    const whatsappNumber = useSetting("whatsapp_number", "351900000000");
    const instagramUrl = useSetting("instagram_url", "https://instagram.com/novifood");
    const footerAbout = useSetting("footer_about", tr(
        "Carrinhos premium para empreendedores que querem trabalhar com elegância, autonomia e estilo.",
        "Premium food carts for entrepreneurs who want to work with elegance, autonomy and style."
    ), { localized: en });

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            const { data } = await api.post("/newsletter", { email });
            toast.success(data.message || tr("Subscrição confirmada!", "Subscription confirmed!"));
            setEmail("");
        } catch (err) {
            const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || tr("Não foi possível subscrever.", "Could not subscribe.");
            toast.error(typeof msg === "string" ? msg : tr("Não foi possível subscrever.", "Could not subscribe."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer className="relative mt-24 overflow-hidden gradient-navy text-white" data-testid="site-footer">
            <div className="absolute inset-0 grain-overlay" />
            <div className="relative container-x py-10 sm:py-12 lg:py-14 grid gap-8 lg:gap-10 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <Logo testId="footer-logo" size="lg" variant="white" />
                    <p className="mt-6 max-w-md text-white/75 leading-relaxed text-[17px] font-light">
                        {footerAbout}
                    </p>
                    <div className="mt-7 flex items-center gap-3">
                        <a href={`mailto:${contactEmail}`} aria-label="Email" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 hover:bg-white/10 hover:border-secondary/50 transition-all duration-300">
                            <Mail className="h-4 w-4" />
                        </a>
                        <a href={`https://wa.me/${whatsappNumber}`} aria-label="WhatsApp" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 hover:bg-white/10 hover:border-secondary/50 transition-all duration-300">
                            <MessageCircle className="h-4 w-4" />
                        </a>
                        <a href={instagramUrl} aria-label="Instagram" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 hover:bg-white/10 hover:border-secondary/50 transition-all duration-300">
                            <Instagram className="h-4 w-4" />
                        </a>
                    </div>
                </div>

                <div className="lg:col-span-3 grid grid-cols-2 gap-8">
                    <div>
                        <p className="inline-flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.28em] text-white/60">
                            <span aria-hidden className="inline-block h-[1.5px] w-6 bg-secondary" />
                            Explorar
                        </p>
                        <ul className="mt-5 space-y-2.5 text-sm text-white/80">
                            <li><Link to="/" data-testid="footer-link-home" className="hover:text-secondary transition">Home</Link></li>
                            <li><Link to="/modelo-alfa" data-testid="footer-link-modelo-alfa" className="hover:text-secondary transition">{tr("Modelo Alfa", "Alfa Model")}</Link></li>
                            <li><Link to="/modelo-omega" data-testid="footer-link-modelo-omega" className="hover:text-secondary transition">{tr("Modelo Ômega", "Omega Model")}</Link></li>
                            <li><Link to="/processos" data-testid="footer-link-processos" className="hover:text-secondary transition">{tr("Processos", "Process")}</Link></li>
                            <li><Link to="/simulador" data-testid="footer-link-simulador" className="hover:text-secondary transition">{tr("Simulador", "Configurator")}</Link></li>
                            <li><Link to="/eventos" data-testid="footer-link-eventos" className="hover:text-secondary transition">{tr("Eventos", "Events")}</Link></li>
                            <li><Link to="/galeria" data-testid="footer-link-galeria" className="hover:text-secondary transition">{tr("Galeria", "Gallery")}</Link></li>
                        </ul>
                    </div>
                    <div>
                        <p className="inline-flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.28em] text-white/60">
                            <span aria-hidden className="inline-block h-[1.5px] w-6 bg-secondary" />
                            {tr("Empresa", "Company")}
                        </p>
                        <ul className="mt-5 space-y-2.5 text-sm text-white/80">
                            <li><Link to="/sobre" data-testid="footer-link-sobre" className="hover:text-secondary transition">{tr("Sobre", "About")}</Link></li>
                            <li><Link to="/faq" data-testid="footer-link-faq" className="hover:text-secondary transition">FAQ</Link></li>
                            <li><Link to="/contacto" data-testid="footer-link-contacto" className="hover:text-secondary transition">{tr("Contacto", "Contact")}</Link></li>
                            <li><a href="#" className="hover:text-secondary transition">{tr("Política de Privacidade", "Privacy Policy")}</a></li>
                            <li><a href="#" className="hover:text-secondary transition">{tr("Termos", "Terms")}</a></li>
                        </ul>
                    </div>
                </div>

                <div className="lg:col-span-4">
                    <p className="inline-flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.28em] text-white/60">
                        <span aria-hidden className="inline-block h-[1.5px] w-6 bg-secondary" />
                        Newsletter
                    </p>
                    <h3 className="mt-4 font-heading text-2xl lg:text-3xl font-bold leading-tight">
                        {tr("Fica a par das ", "Stay up to date with ")}<span className="serif-accent text-secondary">{tr("novidades", "our news")}</span>
                    </h3>
                    <p className="mt-3 text-sm text-white/70 leading-relaxed">
                        {tr(
                            "Inscreve-te e recebe novidades sobre os nossos produtos, condições e facilidades de financiamento.",
                            "Subscribe to receive news about our products, terms and financing options."
                        )}
                    </p>
                    <form onSubmit={submit} className="mt-6 flex items-center gap-2" data-testid="footer-newsletter-form">
                        <input
                            type="email"
                            required
                            placeholder={tr("o-teu-email@exemplo.pt", "your-email@example.com")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="newsletter-email-input"
                            className="w-full rounded-full border border-white/15 bg-white/10 px-5 py-3 text-sm text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-secondary backdrop-blur"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="newsletter-submit-button"
                            className="inline-flex h-[46px] items-center gap-2 rounded-full bg-secondary px-5 text-sm font-bold text-primary shadow-[0_8px_24px_-8px_rgba(224,174,50,0.5)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
                        >
                            {loading ? "..." : tr("Subscrever", "Subscribe")} <ArrowRight className="h-4 w-4" />
                        </button>
                    </form>
                </div>
            </div>

            <div className="relative border-t border-white/10">
                <div className="container-x flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 py-6 text-xs text-white/60">
                    <p>© {new Date().getFullYear()} NOVIFOOD. {tr("Todos os direitos reservados.", "All rights reserved.")}</p>
                    <div className="flex items-center gap-4 flex-wrap">
                        <LocaleSwitch />
                        <p className="font-mono">Made in Portugal · Powered by Sun ☀</p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
